import { Suspense, useRef, type ReactNode } from 'react';
import { useLocation, useRouter } from '@tanstack/react-router';
import {
  Page,
  PageBody,
  PageLoader,
  PageSection,
  TabNav,
  TabNavList,
  TabNavTab,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { ErrorPage } from '@/components/page/error';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { useObjectTypes } from '@/services/db/object-type/queries';
import { useSegmentOptional } from '@/services/db/segment/queries';
import { useSqlImport } from '@/services/db/sql-import/queries';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import { sqlImportSyncTarget } from '@/features/sql-sync';
import { isEmptyString } from '@/utils/string';
import { SQL_SYNC_IMPORT_ROUTE_PATHS, SQL_SYNC_IMPORT_TAB_ROUTE_PATHS, sqlSyncImportTabFromPathname } from '../hooks';
import { SqlImportDetailProvider, type SqlImportTabHrefs } from './context';
import { ImportHeader } from './components/import-header';

const EDIT_PERMISSION = 'journeys/sql_syncs/edit';
const DELETE_PERMISSION = 'journeys/sql_syncs/delete';
const LOADING_LABEL = 'Loading SQL sync…';

type SqlImportDetailPageProps = {
  workspaceId: string;
  type: SqlDatabaseType;
  sqlImportId: string;
  /** The active tab's route. */
  children: ReactNode;
};

/**
 * One import: its name and description, the actions on it, and the tabs — Imports (the run
 * history), the query, the relationship query when it has one, and Settings — around the tab
 * being shown.
 */
export default function SqlImportDetailPage(props: SqlImportDetailPageProps) {
  return (
    <Suspense fallback={<PageLoader label={LOADING_LABEL} />}>
      <SqlImportDetail {...props} />
    </Suspense>
  );
}

function useTabHrefs({
  workspaceId,
  type,
  sqlImportId,
}: Omit<SqlImportDetailPageProps, 'children'>): SqlImportTabHrefs {
  const router = useRouter();
  const params = { workspaceId, type, sqlImportId };
  return {
    imports: router.buildLocation({ to: SQL_SYNC_IMPORT_ROUTE_PATHS.migrated, params }).href,
    query: router.buildLocation({ to: SQL_SYNC_IMPORT_TAB_ROUTE_PATHS.query.migrated, params }).href,
    relationshipsQuery: router.buildLocation({
      to: SQL_SYNC_IMPORT_TAB_ROUTE_PATHS['relationships-query'].migrated,
      params,
    }).href,
    settings: router.buildLocation({ to: SQL_SYNC_IMPORT_TAB_ROUTE_PATHS.settings.migrated, params }).href,
  };
}

function SqlImportDetail({ workspaceId, type, sqlImportId, children }: SqlImportDetailPageProps) {
  const workspace = Number(workspaceId);
  const { hasPermission } = usePermissions();
  const { pathname } = useLocation();
  const { data: sqlImport } = useSqlImport({ id: sqlImportId, workspaceId: workspace });
  const { data: objectTypes } = useObjectTypes({ workspaceId: workspace });
  const environment = useEnvironmentOptional(workspace);
  const segment = useSegmentOptional({
    workspaceId: workspace,
    id: sqlImport.segment_id ?? '',
    enabled: !isEmptyString(sqlImport.segment_id),
  });
  const hrefs = useTabHrefs({ workspaceId, type, sqlImportId });
  // Flipped by a delete, in the same tick it navigates away, before any leave guard is asked.
  const isDeletedRef = useRef(false);

  // The tabs' forms default their identifier from the workspace's identifiers, so they wait; a
  // read that failed is shown as such rather than as a loader that never ends.
  if (environment.isError) {
    return <ErrorPage error={environment.error} />;
  }
  if (environment.data == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  const activeTab = sqlSyncImportTabFromPathname(pathname);
  const syncTarget = sqlImportSyncTarget(sqlImport, objectTypes);
  const hasRelationship = sqlImport.relationship != null;

  return (
    <SqlImportDetailProvider
      value={{
        workspaceId,
        type,
        sqlImportId,
        sqlImport,
        objectTypes,
        syncTarget,
        segment: segment.data == null ? null : { id: segment.data.id, name: segment.data.name },
        canEdit: hasPermission(EDIT_PERMISSION),
        canDelete: hasPermission(DELETE_PERMISSION),
        canIdentifyByEmail: environment.data.environment?.identifiers?.email !== 0,
        hrefs,
        isDeleted: () => isDeletedRef.current,
        markDeleted: () => {
          isDeletedRef.current = true;
        },
      }}
    >
      <Page>
        <PageBody>
          <PageSection>
            <ImportHeader />
            <TabNav mb="300">
              <TabNavList>
                <TabNavTab href={hrefs.imports} isActive={activeTab == null} data-test-sql-imports-tab="tab-imports">
                  Imports
                </TabNavTab>
                <TabNavTab href={hrefs.query} isActive={activeTab === 'query'} data-test-sql-imports-tab="tab-query">
                  {syncTarget} Query
                </TabNavTab>
                {hasRelationship ? (
                  <TabNavTab
                    href={hrefs.relationshipsQuery}
                    isActive={activeTab === 'relationships-query'}
                    data-test-sql-imports-tab="tab-rel-query"
                  >
                    Relationship Query
                  </TabNavTab>
                ) : null}
                <TabNavTab
                  href={hrefs.settings}
                  isActive={activeTab === 'settings'}
                  data-test-sql-imports-tab="tab-settings"
                >
                  Settings
                </TabNavTab>
              </TabNavList>
            </TabNav>
            {children}
          </PageSection>
        </PageBody>
      </Page>
    </SqlImportDetailProvider>
  );
}
